import { useEffect, useLayoutEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { ImagePlus, Pencil, Plus, UserCheck, UserX } from 'lucide-react';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { Card, CardHeader, PageHeader } from '../components/ui/Card';
import { DataTable, type Column } from '../components/ui/DataTable';
import { Checkbox, Input, Select } from '../components/ui/Input';
import { ConfirmDialog, Modal } from '../components/ui/Modal';
import { Tabs } from '../components/ui/Tabs';
import { useCreateUser, usePublicSettings, useUpdateSettings, useUpdateUser, useUsers } from '../hooks/useApi';
import { useUrlFilters } from '../hooks/useUrlFilters';
import { errorMessage } from '../services/api';
import { toast, useAuthStore } from '../store';
import type { Role, User } from '../types';
import { applyPrimaryColor, formatDateTime } from '../utils/helpers';
import { t } from '../i18n';

export function Settings() {
  const [filters, setFilters] = useUrlFilters({ tab: 'users' });
  return (
    <>
      <PageHeader title={t('Nastavenia')} subtitle={t('Používateľské účty a vzhľad aplikácie')} />
      <Tabs
        className="mb-4"
        value={filters.tab}
        onChange={(tab) => setFilters({ tab })}
        tabs={[
          { value: 'users', label: t('Používatelia') },
          { value: 'branding', label: t('Vzhľad a firma') },
        ]}
      />
      {filters.tab === 'branding' ? <BrandingSettings /> : <UsersSettings />}
    </>
  );
}

function UsersSettings() {
  const me = useAuthStore((s) => s.user);
  const { data, isLoading } = useUsers({ page_size: 500 });
  const update = useUpdateUser();
  const [editing, setEditing] = useState<User | null>(null);
  const [creating, setCreating] = useState(false);
  const [toggling, setToggling] = useState<User | null>(null);

  const columns: Column<User>[] = [
    {
      key: 'user',
      header: t('Používateľ'),
      render: (u) => (
        <div>
          <p className="font-medium">{u.full_name || u.username}</p>
          <p className="font-mono text-xs text-secondary-500">{u.username}</p>
        </div>
      ),
    },
    { key: 'role', header: t('Rola'), render: (u) => (u.role === 'admin' ? <Badge variant="primary">{t('Administrátor')}</Badge> : <Badge>{t('Používateľ')}</Badge>) },
    { key: 'status', header: t('Stav'), render: (u) => (u.is_active ? <Badge variant="success">{t('Aktívny')}</Badge> : <Badge variant="danger">{t('Deaktivovaný')}</Badge>) },
    { key: 'login', header: t('Posledné prihlásenie'), className: 'whitespace-nowrap', render: (u) => formatDateTime(u.last_login) },
    { key: 'created', header: t('Vytvorený'), className: 'whitespace-nowrap', render: (u) => formatDateTime(u.created_at) },
    {
      key: 'actions',
      header: <span className="sr-only">{t('Akcie')}</span>,
      className: 'text-right whitespace-nowrap',
      render: (u) => (
        <>
          <button className="icon-btn" onClick={() => setEditing(u)} title={t('Upraviť')} aria-label={t('Upraviť')}>
            <Pencil className="h-4 w-4" />
          </button>
          {u.id !== me?.id && (
            <button
              className={u.is_active ? 'icon-btn-danger' : 'icon-btn'}
              onClick={() => setToggling(u)}
              title={u.is_active ? t('Deaktivovať') : t('Aktivovať')}
              aria-label={u.is_active ? t('Deaktivovať') : t('Aktivovať')}
            >
              {u.is_active ? <UserX className="h-4 w-4" /> : <UserCheck className="h-4 w-4" />}
            </button>
          )}
        </>
      ),
    },
  ];

  return (
    <Card padding="none">
      <div className="flex flex-wrap items-center justify-between gap-3 p-4">
        <p className="text-sm text-secondary-500">
          {t('Administrátor vidí audit log a spravuje používateľov aj vzhľad. Bežný používateľ spravuje vozidlá, vodičov, servisy, dokumenty a importy.')}
        </p>
        <Button onClick={() => setCreating(true)}>
          <Plus className="h-4 w-4" />  {t('Nový používateľ')}
        </Button>
      </div>
      <DataTable data={data?.users} columns={columns} rowKey={(u) => u.id} isLoading={isLoading} rowClassName={(u) => (u.is_active ? undefined : 'text-secondary-400')} />
      <UserFormModal isOpen={creating} onClose={() => setCreating(false)} />
      <UserFormModal isOpen={!!editing} user={editing} onClose={() => setEditing(null)} />
      <ConfirmDialog
        isOpen={!!toggling}
        onClose={() => setToggling(null)}
        loading={update.isPending}
        variant={toggling?.is_active ? 'danger' : 'primary'}
        title={toggling?.is_active ? t('Deaktivovať používateľa') : t('Aktivovať používateľa')}
        confirmText={toggling?.is_active ? t('Deaktivovať') : t('Aktivovať')}
        message={
          toggling?.is_active
            ? t('Používateľ {username} sa nebude môcť prihlásiť. Jeho záznamy v audit logu zostanú zachované.', { username: toggling.username })
            : t('Používateľ {username} sa bude opäť môcť prihlásiť.', { username: toggling?.username })
        }
        onConfirm={async () => {
          try {
            await update.mutateAsync({ id: toggling!.id, data: { is_active: !toggling!.is_active } });
            toast.success(t('Uložené'));
          } catch (err) {
            toast.error(errorMessage(err));
          }
          setToggling(null);
        }}
      />
    </Card>
  );
}

interface UserValues {
  username: string;
  full_name: string;
  role: Role;
  password: string;
  is_active: boolean;
}

function UserFormModal({ isOpen, onClose, user }: { isOpen: boolean; onClose: () => void; user?: User | null }) {
  const create = useCreateUser();
  const update = useUpdateUser();
  const me = useAuthStore((s) => s.user);
  const setMe = useAuthStore((s) => s.setUser);
  const saving = create.isPending || update.isPending;
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<UserValues>();

  useLayoutEffect(() => {
    if (isOpen) {
      reset({ username: user?.username ?? '', full_name: user?.full_name ?? '', role: user?.role ?? 'user', password: '', is_active: user?.is_active ?? true });
    }
  }, [isOpen, user, reset]);

  const onSubmit = async (values: UserValues) => {
    try {
      if (user) {
        const saved = await update.mutateAsync({
          id: user.id,
          data: {
            full_name: values.full_name || null,
            role: values.role,
            is_active: values.is_active,
            ...(values.password ? { password: values.password } : {}),
          },
        });
        if (saved.id === me?.id) setMe(saved);
        toast.success(values.password ? t('Používateľ uložený, heslo bolo zmenené') : t('Používateľ uložený'));
      } else {
        await create.mutateAsync({ username: values.username, full_name: values.full_name || null, role: values.role, password: values.password });
        toast.success(t('Používateľ bol vytvorený'));
      }
      onClose();
    } catch (err) {
      toast.error(errorMessage(err, t('Používateľa sa nepodarilo uložiť')));
    }
  };

  const isSelf = user?.id === me?.id;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      busy={saving}
      title={user ? t('Upraviť používateľa {username}', { username: user.username }) : t('Nový používateľ')}
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={saving}>
            {t('Zrušiť')}
          </Button>
          <Button type="submit" form="user-form" loading={saving}>
            {user ? t('Uložiť') : t('Vytvoriť')}
          </Button>
        </>
      }
    >
      <form id="user-form" onSubmit={handleSubmit(onSubmit)} className="grid grid-cols-1 gap-4 sm:grid-cols-2" noValidate>
        <Input
          label={t('Používateľské meno')}
          required
          disabled={!!user}
          autoComplete="off"
          error={errors.username?.message}
          {...register('username', {
            required: t('Zadajte používateľské meno'),
            minLength: { value: 3, message: t('Aspoň 3 znaky') },
            pattern: { value: /^[a-zA-Z0-9._-]+$/, message: t('Iba písmená bez diakritiky, číslice, . _ -') },
          })}
        />
        <Input label={t('Celé meno')} {...register('full_name')} />
        <Select
          label={t('Rola')}
          disabled={isSelf}
          hint={isSelf ? t('Vlastnú rolu nie je možné zmeniť') : undefined}
          options={[
            { value: 'user', label: t('Používateľ') },
            { value: 'admin', label: t('Administrátor') },
          ]}
          {...register('role')}
        />
        <Input
          label={user ? t('Nové heslo') : t('Heslo')}
          type="password"
          autoComplete="new-password"
          required={!user}
          hint={user ? t('Vyplňte iba pri nastavení nového hesla (používateľ si ho pri prihlásení zmení)') : t('Aspoň 8 znakov')}
          error={errors.password?.message}
          {...register('password', {
            validate: (v) => (!user && !v ? t('Zadajte heslo') : !v || v.length >= 8 || t('Aspoň 8 znakov')),
          })}
        />
        {user && !isSelf && <Checkbox label={t('Účet je aktívny')} {...register('is_active')} />}
      </form>
    </Modal>
  );
}

const MAX_LOGO_BYTES = 500 * 1024;

function BrandingSettings() {
  const { data } = usePublicSettings();
  const update = useUpdateSettings();
  const [companyName, setCompanyName] = useState('');
  const [primaryColor, setPrimaryColor] = useState('#1e40af');
  const [logo, setLogo] = useState('');

  useEffect(() => {
    if (data) {
      setCompanyName(data.company_name);
      setPrimaryColor(data.primary_color);
      setLogo(data.logo_light_url);
    }
  }, [data]);

  // Live preview of the colour; revert if the page is left without saving
  useEffect(() => {
    applyPrimaryColor(primaryColor);
  }, [primaryColor]);
  useEffect(() => () => applyPrimaryColor(data?.primary_color), [data?.primary_color]);

  const onLogoSelected = (file: File | undefined) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) return toast.error(t('Vyberte obrázok (PNG, SVG, JPG)'));
    if (file.size > MAX_LOGO_BYTES) return toast.error(t('Logo môže mať najviac 500 kB'));
    const reader = new FileReader();
    reader.onload = () => setLogo(String(reader.result));
    reader.readAsDataURL(file);
  };

  const save = async () => {
    try {
      await update.mutateAsync({ company_name: companyName.trim(), primary_color: primaryColor, logo_light_url: logo });
      toast.success(t('Nastavenie vzhľadu bolo uložené'));
    } catch (err) {
      toast.error(errorMessage(err, t('Nastavenie sa nepodarilo uložiť')));
    }
  };

  return (
    <div className="grid gap-6 lg:grid-cols-3">
      <Card className="lg:col-span-2">
        <CardHeader title={t('Firma a vzhľad')} subtitle={t('Zobrazuje sa na prihlasovacej stránke, v menu a v titulku okna')} />
        <div className="space-y-5">
          <Input label={t('Názov spoločnosti / aplikácie')} value={companyName} maxLength={100} onChange={(e) => setCompanyName(e.target.value)} />
          <div>
            <label className="mb-1 block text-sm font-medium text-secondary-700" htmlFor="primary-color">
              {t('Hlavná farba')}
            </label>
            <div className="flex items-center gap-3">
              <input
                id="primary-color"
                type="color"
                value={primaryColor}
                onChange={(e) => setPrimaryColor(e.target.value)}
                className="h-10 w-14 cursor-pointer rounded border border-secondary-300 bg-white p-1"
              />
              <Input
                aria-label={t('Hex kód farby')}
                wrapperClassName="w-32"
                className="font-mono"
                value={primaryColor}
                onChange={(e) => /^#[0-9a-fA-F]{0,6}$/.test(e.target.value) && setPrimaryColor(e.target.value)}
              />
              <div className="flex gap-1" aria-hidden="true">
                {[100, 300, 500, 600, 700, 900].map((s) => (
                  <span key={s} className="h-8 w-6 rounded" style={{ backgroundColor: `rgb(var(--color-primary-${s}))` }} />
                ))}
              </div>
            </div>
          </div>
          <div>
            <p className="mb-1 text-sm font-medium text-secondary-700">{t('Logo')}</p>
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex h-16 w-40 items-center justify-center rounded-lg border border-dashed border-secondary-300 bg-secondary-50">
                {logo ? <img src={logo} alt={t('Logo')} className="max-h-14 max-w-[9rem] object-contain" /> : <span className="text-xs text-secondary-400">{t('Bez loga')}</span>}
              </div>
              <label className="inline-flex cursor-pointer items-center gap-2 rounded-lg border border-secondary-300 bg-white px-3 py-1.5 text-sm font-medium text-secondary-700 hover:bg-secondary-50">
                <ImagePlus className="h-4 w-4" />  {t('Vybrať obrázok')}
                <input type="file" accept="image/*" className="sr-only" onChange={(e) => onLogoSelected(e.target.files?.[0])} />
              </label>
              {logo && (
                <Button variant="ghost" size="sm" onClick={() => setLogo('')}>
                  {t('Odobrať')}
                </Button>
              )}
            </div>
            <p className="mt-1 text-xs text-secondary-500">{t('PNG alebo SVG s priehľadným pozadím, max. 500 kB.')}</p>
          </div>
          <Button onClick={save} loading={update.isPending}>
            {t('Uložiť nastavenie')}
          </Button>
        </div>
      </Card>
      <Card>
        <CardHeader title={t('Náhľad tlačidiel')} />
        <div className="space-y-3">
          <Button className="w-full">{t('Hlavná akcia')}</Button>
          <Button variant="outline" className="w-full">
            {t('Vedľajšia akcia')}
          </Button>
          <div className="rounded-lg bg-primary-50 p-3 text-sm text-primary-700">{t('Zvýraznená položka menu')}</div>
        </div>
      </Card>
    </div>
  );
}
